/**
 * Routes Configuration - Frontend
 * 
 * Construit la configuration enrichie des routes à partir de la base commune.
 * Chaque route est enrichie avec:
 * - Icons (FontAwesome)
 * - URLs (depuis environment)
 * - Circuit-breaker configuration
 */

import { 
  faFileLines, 
  faEye, 
  faGlobe, 
  faHammer, 
  faDatabase,
  faComments
} from '@fortawesome/free-solid-svg-icons';

import { buildFromBase, getConfigForEndpoint } from '@commons-shared/routes-resolver.js';
import { ROUTES_CONFIG_BASE } from '@commons-shared/routes-config-base.js';

/**
 * Configuration par défaut des circuit breakers pour toutes les routes
 */
export const DEFAULT_CIRCUIT_BREAKER_CONFIG = {
  failureThreshold: 3,
  recoveryTimeout: 30000,
  healthCheckInterval: 30000,
  timeout: 5000,
  checkHealthStrategy: 'onCalls',
  recoveryStrategy: 'onCalls',
  watchdogEnabled: true,
  watchdogThreshold: 3,
  watchdogTimeWindow: 2000,
  watchdogCooldown: 10000
};

/**
 * Mapping des icônes
 */
const ROUTE_ICONS = {
  TEXT: faFileLines,
  VISION: faEye,
  TRANSLATION: faGlobe,
  WA: faHammer,
  CONFIG: faDatabase,
  CHAT: faComments
};

/**
 * Configuration spécifique au circuit-breaker par type de route
 */
function getCircuitBreakerConfigForType(type) {
  const configs = {
    TEXT: {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      failureThreshold: 3,
      timeout: 6000
    },
    VISION: {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      timeout: 12000,
      failureThreshold: 4
    },
    TRANSLATION: {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      timeout: 10000,
      failureThreshold: 5
    },
    WA: {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      recoveryTimeout: 20000,
      healthCheckInterval: 15000,
      watchdogThreshold: 50
    },
    CONFIG: {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      recoveryTimeout: 20000,
      healthCheckInterval: 15000,
      watchdogThreshold: 30
    },
    CHAT: {
      ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
      failureThreshold: 4,
      timeout: 8000,
      recoveryTimeout: 25000,
      watchdogThreshold: 30
    }
  };

  return configs[type] || DEFAULT_CIRCUIT_BREAKER_CONFIG;
}

/**
 * Construire la configuration enrichie des routes
 * buildFromBase utilise automatiquement workers-config.js si baseUrls n'est pas fourni
 * Chaque route est enrichie avec icons et circuit-breaker config
 */
export const ROUTES_CONFIG = ROUTES_CONFIG_BASE.map(baseRoute => {
  const type = baseRoute.type;
  
  return buildFromBase(type, ROUTES_CONFIG_BASE, {
    icons: ROUTE_ICONS,
    circuitConfig: getCircuitBreakerConfigForType(type)
  });
});

/**
 * Obtenir la configuration d'une route par son type
 * @param {string} type - Le type de route (TEXT, VISION, TRANSLATION, WA, CONFIG, CHAT)
 * @returns {Object|null} La configuration de la route ou null
 */
export function getRouteConfigByType(type) {
  return ROUTES_CONFIG.find(config => config.type === type) || null;
}

/**
 * Obtenir la configuration d'une route par sa route
 * @param {string} route - La route API
 * @returns {Object|null} La configuration de la route ou null
 */
export function getRouteConfigByRoute(route) {
  return ROUTES_CONFIG.find(config => config.route === route) || null;
}

/**
 * Obtenir toutes les routes
 * @returns {string[]} Liste de toutes les routes
 */
export function getAllRoutes() {
  return ROUTES_CONFIG.map(config => config.route);
}

/**
 * Obtenir la configuration de circuit breaker pour une route
 * @param {string} route - La route API
 * @returns {Object|null} La configuration du circuit breaker ou null
 */
export function getCircuitBreakerConfig(route) {
  const routeConfig = getRouteConfigByRoute(route);
  return routeConfig ? routeConfig.circuitBreakerConfig : null;
}

/**
 * Obtenir la configuration complète d'un worker par endpoint
 * @param {string} endpoint - L'endpoint API
 * @returns {Object} Configuration du worker (baseUrl, name, circuitRoute)
 */
export function getWorkerConfig(endpoint) {
  const fullConfig = getConfigForEndpoint(endpoint, ROUTES_CONFIG);
  
  if (fullConfig) {
    return {
      baseUrl: fullConfig.baseUrl,
      name: fullConfig.workerName,
      circuitRoute: fullConfig.circuitRoute
    };
  }

  // Fallback sur /api/config
  const defaultConfig = ROUTES_CONFIG.find(config => config.route === '/api/config');
  return defaultConfig ? {
    baseUrl: defaultConfig.baseUrl,
    name: defaultConfig.workerName,
    circuitRoute: defaultConfig.circuitRoute
  } : null;
}

